import { useEffect, type ReactElement } from 'react'
import { useSearchParams } from 'react-router-dom'
import { StationDetails, type StationDetailsProps } from '@metro/station-details'
import styles from './StationDetailsVisualFixture.module.css'

/**
 * Private QA fixture for Playwright phone-frame snapshots of
 * StationDetails (spec 2026-05-25, Figma section 6967:29525). Mounts a
 * single state inside a fixed 402×874 frame (the Figma phone frame) at
 * each theme, so every screenshot lands pixel-stable and can be diffed
 * against the Figma PNG baselines under
 * `packages/station-details/figma-baselines/phone/{theme}/S{n}.png`.
 *
 * URL contract:
 *   /__visual/station-details?state=<S1|S2|S3|S4|S5>&theme=<light|dark>
 *
 *   S1 — Full (non-premium): upsell banner + transfers + map + facilities
 *   S2 — Premium + Timetable: timetable instead of upsell
 *   S3 — Loading skeleton
 *   S4 — Error + Retry
 *   S5 — Offline (S1 body + offline banner after header)
 *
 * Dev/QA only — excluded from robots.txt (`Disallow: /__visual/`).
 */

type FixtureState = 'S1' | 'S2' | 'S3' | 'S4' | 'S5'

const VALID: ReadonlySet<FixtureState> = new Set<FixtureState>(['S1', 'S2', 'S3', 'S4', 'S5'])

function parseState(raw: string | null): FixtureState {
  if (raw !== null && (VALID as Set<string>).has(raw)) return raw as FixtureState
  return 'S1'
}

const BASE: StationDetailsProps = {
  stationId: 'baker-street',
  stationName: 'Baker Street',
  lineLocalName: 'Bakerloo line',
  lineHexColor: '#B36305',
  zone: '1',
  lat: 0,
  lng: 0,
  facilities: ['WIFI', 'LIFTS', 'TOILETS'],
  timetable: {
    directions: [
      {
        headSign: 'NB to Stanmore',
        weekdays: { firstTrain: '05:30', lastTrain: '00:30' },
        saturdays: { firstTrain: '05:55', lastTrain: '00:41' },
        sundays: { firstTrain: '07:34', lastTrain: '23:55' },
      },
    ],
  },
  transfers: [
    {
      id: 'baker-street-met',
      stationName: 'Baker Street (Met)',
      lineLocalName: 'Metropolitan',
      lineHexColor: '#9B0056',
    },
  ],
  isFavorite: false,
  isPremium: false,
  onTransferTap: () => {},
  onFavoriteTap: () => {},
  onPaywallTap: () => {},
  onReportIssueTap: () => {},
  onRetry: () => {},
}

function propsForState(state: FixtureState): StationDetailsProps {
  switch (state) {
    case 'S1':
      return BASE
    case 'S2':
      return { ...BASE, isPremium: true }
    case 'S3':
      return { ...BASE, hasStation: false, isLoading: true }
    case 'S4':
      return { ...BASE, hasStation: false, isLoading: false }
    case 'S5':
      return { ...BASE, isOffline: true }
  }
}

export function StationDetailsVisualFixture(): ReactElement {
  const [params] = useSearchParams()
  const state = parseState(params.get('state'))
  const theme = params.get('theme') === 'dark' ? 'dark' : 'light'

  useEffect(() => {
    const html = document.documentElement
    const prev = html.dataset.theme
    html.dataset.theme = theme
    html.classList.add('visual-fixture-active')
    return () => {
      if (prev === undefined) delete html.dataset.theme
      else html.dataset.theme = prev
      html.classList.remove('visual-fixture-active')
    }
  }, [theme])

  return (
    <div
      className={styles.frame}
      data-testid="station-details-visual-fixture"
      data-state={state}
      data-theme={theme}
    >
      <StationDetails {...propsForState(state)} />
    </div>
  )
}

export default StationDetailsVisualFixture
